import React from 'react';
import DraftBanner from '../components/DraftBanner';
import LegalLayout from '../components/LegalLayout';

export default function Terms() {
  return (
    <>
      <DraftBanner documentName="Terms of Service" />

      <LegalLayout
        eyebrow="Terms of Service"
        title="The rules that govern using OfficerCapital."
        lastUpdated="Draft — pre-launch"
        intro="By using OfficerCapital you agree to these Terms. They define what you can do on the platform, what we'll do for you, and what happens when something goes wrong. If you don't agree with any of this, don't use the platform."
        sections={[
          {
            id: 'acceptance',
            title: 'Acceptance of these Terms',
            body: (
              <>
                <p>
                  These Terms of Service ("Terms") form a binding contract between you and OfficerCapital, Inc.
                  ("OfficerCapital", "we", "us"). By creating an account, listing a company, or applying for partner
                  access, you agree to these Terms.
                </p>
                <p>
                  If you are using OfficerCapital on behalf of a business, you represent that you are authorized to bind
                  that business to these Terms.
                </p>
              </>
            ),
          },
          {
            id: 'eligibility',
            title: 'Eligibility',
            body: (
              <>
                <p>To use the platform, you must:</p>
                <ul>
                  <li>Be at least 18 years old.</li>
                  <li>Have the legal authority to enter contracts on behalf of yourself or the business you represent.</li>
                  <li>Not be barred from using the service under applicable law.</li>
                </ul>
                <p>
                  Owner accounts are intended for security guard companies and businesses that operate substantially in
                  the security guard industry. Capital partner accounts are intended for legitimate lenders, investors,
                  and acquirers, subject to the manual application review described on our Partners page.
                </p>
              </>
            ),
          },
          {
            id: 'accounts',
            title: 'Your account',
            body: (
              <>
                <p>
                  You're responsible for the accuracy of the information you provide and for keeping your password
                  confidential. You're responsible for activity that happens on your account.
                </p>
                <p>
                  Notify us immediately at <a href="mailto:security@officercapital.com" style={{ color: 'var(--oc-green-deep)' }}>security@officercapital.com</a>{' '}
                  if you believe your account has been accessed without your authorization.
                </p>
              </>
            ),
          },
          {
            id: 'owners',
            title: 'Rules for owners',
            body: (
              <>
                <p>If you list a company on OfficerCapital, you agree that:</p>
                <ul>
                  <li>The information you provide on your listing is accurate and not misleading. You will correct material errors promptly.</li>
                  <li>You have the authority to list the company — for example, you are the owner, an authorized officer, or have written authorization from the owner.</li>
                  <li>You will not list the same company through multiple accounts to inflate visibility or game the access system.</li>
                  <li>You will not list a company purely to extract competitive intelligence about capital partners with no genuine intent to engage.</li>
                </ul>
                <p>
                  You retain ownership of the information you submit. You grant us a limited license to display
                  anonymized listing data to vetted capital partners and to display unlocked listing data to capital
                  partners you specifically approve.
                </p>
              </>
            ),
          },
          {
            id: 'partners',
            title: 'Rules for capital partners',
            body: (
              <>
                <p>If you have a partner account (lender, investor, or acquirer), you agree that:</p>
                <ul>
                  <li>
                    <strong>No external sharing.</strong> Listing information — anonymized or unlocked — must not leave
                    your firm. You may share within your firm with employees and advisors who have a legitimate need to
                    know and who are bound by confidentiality. You may not share with anyone outside your firm.
                  </li>
                  <li>
                    <strong>No off-platform circumvention.</strong> You will not use information learned on
                    OfficerCapital to identify and approach the underlying owner outside of the approved-access flow.
                  </li>
                  <li>
                    <strong>Honest profile.</strong> Your firm's profile, deal history, and references must be accurate.
                    Material misrepresentation is grounds for immediate removal and forfeiture of fees paid.
                  </li>
                  <li>
                    <strong>Use of unlock access.</strong> The unlock fee buys you access to the listing file for
                    evaluation. It does not entitle you to a response from the owner, a meeting, or any specific outcome.
                  </li>
                </ul>
                <p>
                  Violation of these rules may result in account suspension, permanent removal, forfeiture of fees, and
                  liability for damages caused to owners or to OfficerCapital.
                </p>
              </>
            ),
          },
          {
            id: 'fees',
            title: 'Fees and payments',
            body: (
              <>
                <h3>For owners</h3>
                <p>
                  Listing on OfficerCapital is free for owners. There are no listing fees, no per-message charges, and
                  no success fees. We do not take a percentage of your financing, your investment, or your sale price.
                </p>

                <h3>For capital partners</h3>
                <p>
                  Capital partners pay a monthly subscription for marketplace access and a per-listing unlock fee for
                  any specific listing they choose to evaluate. Current fee amounts are published on the{' '}
                  <a href="/pricing" style={{ color: 'var(--oc-green-deep)' }}>Pricing page</a>. We may change fees with
                  reasonable advance notice.
                </p>

                <h3>Refunds</h3>
                <p>
                  Subscription fees are not refundable except where required by law. Unlock fees are not refundable
                  if the owner declines to approve access; the unlock fee buys access to the listing, not a guaranteed
                  conversation.
                </p>

                <h3>Payment processing</h3>
                <p>
                  Payments are processed by a third-party payment provider. By providing payment information, you agree
                  to that provider's terms in addition to ours.
                </p>
              </>
            ),
          },
          {
            id: 'role',
            title: "OfficerCapital's role",
            body: (
              <>
                <p>
                  We are an introduction layer. We do not represent owners as a broker or agent. We do not represent
                  capital partners. We do not negotiate, advise on, or close deals — we put parties in front of one
                  another, and the rest is between you.
                </p>
                <p>
                  Nothing on OfficerCapital is investment advice, legal advice, accounting advice, or a recommendation
                  to enter or refrain from any transaction. You're responsible for your own diligence and your own
                  professional advisors.
                </p>
              </>
            ),
          },
          {
            id: 'prohibited',
            title: 'Prohibited conduct',
            body: (
              <>
                <p>You agree not to:</p>
                <ul>
                  <li>Use the platform for unlawful purposes or in violation of these Terms.</li>
                  <li>Attempt to gain unauthorized access to other users' accounts or the platform's systems.</li>
                  <li>Scrape, copy, or reproduce listings or partner profiles outside the platform's intended use.</li>
                  <li>Upload malware, run automated tools without our written permission, or interfere with the platform's operation.</li>
                  <li>Misrepresent your identity, your firm, or your authority to act.</li>
                  <li>Harass, threaten, or send unsolicited promotional content to other users.</li>
                </ul>
              </>
            ),
          },
          {
            id: 'termination',
            title: 'Suspension and termination',
            body: (
              <>
                <p>
                  We may suspend or terminate your account at any time for violation of these Terms, suspected fraud,
                  legal compulsion, or to protect the platform and its users. We will give reasonable notice when
                  practical and not contrary to the reason for termination.
                </p>
                <p>
                  You can terminate your account at any time through account settings. Termination does not relieve you
                  of obligations that have already accrued, including any unpaid fees.
                </p>
              </>
            ),
          },
          {
            id: 'warranties',
            title: 'Disclaimers',
            body: (
              <p>
                The platform is provided "as is" and "as available" without warranties of any kind, whether express or
                implied, except where prohibited by law. We do not warrant that the platform will be uninterrupted, error-free,
                or free from harmful components. We do not endorse, verify the operating performance of, or guarantee any
                particular outcome with any owner or capital partner you connect with.
              </p>
            ),
          },
          {
            id: 'liability',
            title: 'Limitation of liability',
            body: (
              <>
                <p>
                  To the maximum extent permitted by law, OfficerCapital and its officers, employees, and agents are
                  not liable for indirect, incidental, special, consequential, or punitive damages arising from or in
                  connection with your use of the platform.
                </p>
                <p>
                  Our aggregate liability for any claim arising under or in connection with these Terms is capped at
                  the greater of (a) the fees you paid to us in the 12 months preceding the event giving rise to the
                  claim, or (b) one hundred U.S. dollars (US$100). Some jurisdictions do not allow these limitations,
                  and they may not apply to you.
                </p>
              </>
            ),
          },
          {
            id: 'indemnity',
            title: 'Indemnification',
            body: (
              <p>
                You agree to indemnify and hold OfficerCapital harmless from claims, damages, and expenses (including
                reasonable attorneys' fees) arising from your use of the platform, your content, or your violation of
                these Terms or applicable law.
              </p>
            ),
          },
          {
            id: 'disputes',
            title: 'Disputes and governing law',
            body: (
              <>
                <p>
                  These Terms are governed by the laws of the State of [JURISDICTION TBD], without regard to its
                  conflict-of-laws principles. The exclusive jurisdiction and venue for any dispute is the state and
                  federal courts located in [COUNTY/CITY TBD].
                </p>
                <p>
                  Before filing a formal claim, you agree to first contact us at{' '}
                  <a href="mailto:legal@officercapital.com" style={{ color: 'var(--oc-green-deep)' }}>legal@officercapital.com</a>{' '}
                  and attempt to resolve the dispute informally for at least 30 days.
                </p>
              </>
            ),
          },
          {
            id: 'changes',
            title: 'Changes to these Terms',
            body: (
              <p>
                We may update these Terms. When we do, we will update the "Last updated" date at the top and, for
                material changes, notify users by email or platform notice. Continued use of the platform after a
                change takes effect constitutes acceptance of the updated Terms.
              </p>
            ),
          },
          {
            id: 'misc',
            title: 'Miscellaneous',
            body: (
              <ul>
                <li>If any part of these Terms is held unenforceable, the rest remains in effect.</li>
                <li>Our failure to enforce a provision is not a waiver of our right to enforce it later.</li>
                <li>You may not transfer or assign these Terms without our written consent. We may assign them in connection with a corporate transaction.</li>
                <li>These Terms are the entire agreement between you and us regarding the platform.</li>
              </ul>
            ),
          },
          {
            id: 'contact',
            title: 'Contact',
            body: (
              <p>
                Questions about these Terms can be sent to{' '}
                <a href="mailto:legal@officercapital.com" style={{ color: 'var(--oc-green-deep)' }}>legal@officercapital.com</a>.
              </p>
            ),
          },
        ]}
      />
    </>
  );
}
